import React, { useState, useEffect, useRef } from 'react'; // 1. Added useRef
import { addOrUpdateColumnData } from '../../services/api/GlobalSettingsApi';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { FaTrash, FaPen, FaTimes, FaSave } from 'react-icons/fa';
import DeleteConfirmationModal from '../DeleteConfirmationModal/DeleteConfirmationModal';
import './RenderCustomColumns.scss';

// ... helper functions remain the same ...
const getMaxLength = (datatype) => {
    if (!datatype) return undefined;
    const match = datatype.match(/\((\d+)\)/);
    return match ? parseInt(match[1], 10) : undefined;
};

const formatLabel = (name) => {
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1');
};

export const RenderCustomColumns = ({ caseSettings, courtRef, caseType, existingData, isReadOnly = false }) => {
    const { role } = useAuth();
    const { showToast } = useToast();

    const [currentInputs, setCurrentInputs] = useState({});
    const [committedData, setCommittedData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // 2. Ref to hold the first input element
    const firstInputRef = useRef(null);

    const canEdit = role !== 'customer' && !isReadOnly;

    useEffect(() => {
        initializeData();
    }, [caseSettings, existingData]);

    // 3. Effect to auto-focus the first input when entering edit mode
    useEffect(() => {
        if (isEditing && firstInputRef.current) {
            // Small timeout ensures the DOM is fully rendered before focusing
            setTimeout(() => {
                firstInputRef.current.focus();
            }, 0);
        }
    }, [isEditing]);

    const initializeData = () => {
        if (caseSettings?.columns) {
            const initialInputs = {};

            caseSettings.columns.forEach(col => {
                initialInputs[col.name] = '';
            });

            if (existingData?.columns && Array.isArray(existingData.columns)) {
                existingData.columns.forEach(({ name, value }) => {
                    if (initialInputs.hasOwnProperty(name)) {
                        initialInputs[name] = value || '';
                    }
                });
            }

            setCurrentInputs(initialInputs);
            setCommittedData(initialInputs);
        }
    };

    const handleInputChange = (columnName, value) => {
        setCurrentInputs(prev => ({
            ...prev,
            [columnName]: value
        }));
    };

    // 4. Handle Enter key press
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default form submission if wrapped in <form>
            handleSave();
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const columns = caseSettings.columns.map(col => ({
                name: col.name,
                value: (currentInputs[col.name] || '').trim()
            }));

            await addOrUpdateColumnData({
                courtRef,
                caseType,
                columns
            });

            showToast('Data saved successfully', 'success');
            setCommittedData({ ...currentInputs });
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving data:', error);
            showToast('Failed to save data', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDeleteAll = async () => {
        try {
            await addOrUpdateColumnData({
                courtRef,
                caseType,
                columns: null
            });

            const cleared = {};
            caseSettings.columns.forEach(col => {
                cleared[col.name] = '';
            });

            setCurrentInputs(cleared);
            setCommittedData(cleared);
            showToast('All custom data deleted', 'success');
            setShowDeleteModal(false);
            setIsEditing(false);
        } catch (error) {
            console.error('Error deleting data:', error);
            showToast('Failed to delete data', 'error');
        }
    };

    const toggleEditMode = () => {
        if (isEditing) {
            setCurrentInputs({ ...committedData }); 
        }
        setIsEditing(!isEditing);
    };

    if (!caseSettings?.columns || caseSettings.columns.length === 0) {
        return (
            <div className="tab-content">
                <div className="no-data-container">
                    <h3 className="no-data-title">No Custom Columns Configured</h3>
                </div>
            </div>
        );
    }

    return (
        <div className={`custom-columns-container ${isEditing ? 'mode-edit' : 'mode-view'}`}>
            <div className="custom-columns-header">
                <div className="header-content">
                    <h3>Custom Columns</h3>
                    <p className="header-description">
                        {isEditing
                            ? 'Update the field values below'
                            : 'Review custom case details'}
                    </p>
                </div>

                {canEdit && (
                    <div className="header-actions">
                        {isEditing ? (
                            <button className="cancel-btn" onClick={toggleEditMode} disabled={isSaving}>
                                <FaTimes /> Cancel
                            </button>
                        ) : (
                            <button className="edit-btn" onClick={toggleEditMode}>
                                <FaPen /> Edit
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="fields-list">
                {/* 5. Added index to map arguments */}
                {caseSettings.columns.map((column, index) => {
                    const maxLength = getMaxLength(column.datatype);
                    const value = currentInputs[column.name] || '';
                    const label = formatLabel(column.name);

                    return (
                        <div key={column.name} className="field-row">
                            <div className="field-label">
                                {label}
                                {isEditing && maxLength && (
                                    <span className="max-length-hint">Max {maxLength} chars</span>
                                )}
                            </div>

                            <div className="field-value">
                                {!isEditing ? (
                                    <div className="view-value">
                                        {value ? value : <span className="empty-value">Not specified</span>}
                                    </div>
                                ) : (
                                    <input
                                        // 6. Attach Ref to the FIRST input only
                                        ref={index === 0 ? firstInputRef : null}
                                        type="text"
                                        value={value}
                                        onChange={(e) => handleInputChange(column.name, e.target.value)}
                                        // 7. Attach Enter key handler
                                        onKeyDown={handleKeyDown}
                                        placeholder={`Enter ${label.toLowerCase()}`}
                                        maxLength={maxLength}
                                        className="edit-input"
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isEditing && (
                <div className="actions-bar">
                   {/* ... buttons remain same ... */}
                   <button
                        className="clear-all-btn"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={isSaving}
                    >
                        <FaTrash /> Clear All Data
                    </button>

                    <div className="save-cancel-group">
                        <button
                            className="save-btn"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            <FaSave />
                            {isSaving ? 'Saving Changes...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                itemType="all custom column data"
                onConfirm={confirmDeleteAll}
                onCancel={() => setShowDeleteModal(false)}
            />
        </div>
    );
};